import { formatRupees } from './money'

/**
 * Display bits shared by both remediation screens — the admin tracker
 * (AccountantRemediation.jsx) and an accountant's own My Ledgers — so the
 * two never disagree on what a category is called or how an amount reads.
 */

/** The backend's routing categories (constants.js#REMEDIATION_CATEGORIES),
 *  in the words the accounts team actually uses for them. */
export const CATEGORY_LABEL = {
  BILLING: 'Billing',
  TDS: 'TDS',
  CASH_BANK: 'Cash / Bank',
  OTHER: 'Other',
}

export const categoryLabel = (category) => CATEGORY_LABEL[category] ?? 'Uncategorised'

/** Stored in whole rupees on a remediation item (not paise, unlike the
 *  collector's ledger figures) — converted once here so it reads the same
 *  `₹4,200` way every other amount in the app does. */
export function amountText(rupees) {
  if (rupees === null || rupees === undefined || Number.isNaN(Number(rupees))) return null
  return formatRupees(Math.round(Number(rupees) * 100), { showPaise: false })
}

/**
 * Where an accountant is with a fix — `remediationItem.model.js#status`.
 * `open` is the stored name for "not started" (items raised before Start
 * existed keep reading correctly).
 */
export const WORK_STATUS = {
  open: { label: 'Not started', tone: 'p-mute' },
  in_progress: { label: 'In progress', tone: 'p-info' },
  paused: { label: 'Paused', tone: 'p-warn' },
  confirmed: { label: 'Done', tone: 'p-ok' },
}

export const isDone = (item) => item?.status === 'confirmed'

/** Working time so far — the banked `workedMs` plus the stretch running now, if any. */
export function workedMsNow(item, now = Date.now()) {
  const banked = Number(item?.workedMs) || 0
  if (item?.status !== 'in_progress' || !item.lastResumedAt) return banked
  return banked + Math.max(0, now - new Date(item.lastResumedAt).getTime())
}

/** `12m`, `1h 20m`, `2d 3h` — coarse on purpose; this is effort, not billing. */
export function durationText(ms) {
  const m = Math.floor((Number(ms) || 0) / 60_000)
  if (m < 1) return 'under a minute'
  if (m < 60) return `${m}m`
  const h = Math.floor(m / 60)
  if (h < 24) return m % 60 ? `${h}h ${m % 60}m` : `${h}h`
  const d = Math.floor(h / 24)
  return h % 24 ? `${d}d ${h % 24}h` : `${d}d`
}

/** The item timeline's own words for each recorded action. */
export const HISTORY_LABEL = {
  start: 'Started',
  pause: 'Paused',
  resume: 'Resumed',
  done: 'Marked done',
  reassign: 'Moved to cover',
  return: 'Returned to owner',
}

/**
 * The item's history read back as work sessions and pauses.
 *
 *   session — from a Start/Resume to the next Pause/Done (or still running)
 *   pause   — from a Pause to the next Resume/Done (or still paused)
 *
 * Durations are worked out against `now` for whatever is still open, so the
 * detail page's numbers move with the clock.
 */
export function workSessions(item, now = Date.now()) {
  const sessions = []
  const pauses = []
  let open = null
  let paused = null
  const t = (iso) => new Date(iso).getTime()

  for (const h of item?.history ?? []) {
    if (h.action === 'start' || h.action === 'resume') {
      if (paused) {
        pauses.push({ from: paused.at, to: h.at, note: paused.note, ms: t(h.at) - t(paused.at) })
        paused = null
      }
      open = { from: h.at, kind: h.action }
    } else if (h.action === 'pause' || h.action === 'done') {
      if (open) {
        sessions.push({ from: open.from, to: h.at, kind: open.kind, endedBy: h.action, ms: t(h.at) - t(open.from) })
        open = null
      }
      if (h.action === 'pause') paused = { at: h.at, note: h.note }
      else if (paused) {
        pauses.push({ from: paused.at, to: h.at, note: paused.note, ms: t(h.at) - t(paused.at) })
        paused = null
      }
    }
  }
  if (open) sessions.push({ from: open.from, to: null, kind: open.kind, endedBy: null, ms: now - t(open.from) })
  if (paused) pauses.push({ from: paused.at, to: null, note: paused.note, ms: now - t(paused.at) })

  return {
    sessions,
    pauses,
    pausedMs: pauses.reduce((a, p) => a + Math.max(0, p.ms), 0),
  }
}
